'use client'
import React from 'react'
import classNames from 'classnames'
import { BaseCarousel } from 'components/Carousels/BaseCarousel'

/**
 * Maps legacy Carousel variant to BaseCarousel slide/track classes.
 */
const VARIANT_SLIDE_CLASSES = {
  standard: 'w-full h-full overflow-hidden',
  auto: 'w-auto h-full flex pl-4 last:pr-4 md:pl-8 lg:first:pl-[104px] lg:last:pr-4',
  'preview-full': 'w-full mx-2 my-0',
  preview: 'w-[85%] mx-2 my-0 md:w-[65%] lg:w-[72%] lg:mx-4 lg:my-0 lg:last:mr-[152px] xl:w-[70%]',
  'preview-small':
    'w-4/5 mx-2 my-0 md:w-[32%] lg:w-[30%] lg:mx-6 lg:my-0 lg:last:mr-[152px] lg:first:pl-[88px] xl:w-[27.5%] max-xl:[&_div]:bg-transparent',
  'preview-small-xs':
    'w-4/5 mx-2 my-0 md:w-[90%] lg:w-[30%] lg:mx-6 lg:my-0 lg:last:mr-[152px] lg:first:pl-[88px] xl:w-[27.5%] max-xl:[&_div]:bg-transparent',
  'preview-stretch':
    'w-4/5 flex flex-col m-0 md:w-[32%] lg:w-[30%] lg:m-0 lg:first:pl-[88px] xl:w-[27.5%]',
  'preview-scroll':
    'w-full flex flex-col m-0 md:w-3/5 lg:w-2/5 lg:m-0 lg:first:pl-[88px] sm:w-4/5 xl:w-4/5',
  center: 'w-full flex flex-col items-center justify-center',
}

const VARIANT_CONTAINER_CLASSES = {
  standard: 'lg:px-6',
  preview: 'lg:pl-24',
  'preview-small': 'lg:pl-0',
  'preview-small-xs': 'lg:pl-0',
  'preview-stretch': 'lg:pl-0',
  'preview-scroll': 'lg:pl-0',
}

/**
 * Legacy compatibility wrapper for the old Carousel component.
 * Maps the previous API (carouselItems, itemComponent, variant, etc.) to BaseCarousel.
 */
/* eslint-disable @typescript-eslint/no-unused-vars -- Unused props kept for API compatibility */
export function Carousel({
  autoPlay = false,
  carouselItems,
  centerControlsUltra = false,
  className,
  classNameActiveDot,
  classNameContainer,
  classNameControls,
  classNameIndicatorDots,
  classNameIndicators,
  classNameItems,
  classNameWrapper,
  currentClassName,
  dataItem = {},
  disableArrows = false,
  hiddenControls = false,
  idPrefix = 'carousel',
  infinityLoop = false,
  interval = 4000,
  itemComponent: ItemComponent,
  variant = 'standard',
  withIndicators = false,
  variationDot = null,
  peek = false,
  withBadges,
  carouselThreshold,
  infinityLoopForever,
  setValue,
  cbOnChange,
  navigateOnClick,
  scrollOnChange,
}) {
  /* eslint-enable @typescript-eslint/no-unused-vars */
  const items = carouselItems || []

  if (!items.length || !ItemComponent) return null

  const MemoizedItemComponent = React.memo(ItemComponent)

  const renderItem = (item, index, isActive) => (
    <MemoizedItemComponent
      item={item}
      idPrefix={idPrefix}
      data-testid={`${idPrefix}-item`}
      selected={isActive}
      index={index}
      {...dataItem}
    />
  )

  const variantSlideClass = VARIANT_SLIDE_CLASSES[variant] ?? ''
  const variantContainerClass = VARIANT_CONTAINER_CLASSES[variant] ?? ''
  const centerClass = variant === 'center' && peek ? 'w-[95%] sm:w-full' : ''

  return (
    <div
      id={`carousel-${idPrefix}`}
      data-arrow={!disableArrows}
      className={classNames(
        className,
        'relative flex justify-center w-full pl-4 pr-0 py-0 lg:p-0',
        variantContainerClass,
        'group'
      )}
    >
      <BaseCarousel
        items={items}
        renderItem={renderItem}
        autoPlay={autoPlay}
        interval={interval}
        loop={infinityLoop}
        hideControls={disableArrows || hiddenControls}
        hideIndicators={!withIndicators}
        classNameContainer={classNames(
          classNameContainer,
          centerControlsUltra &&
            '[&>div:first-of-type]:left-[calc(50%-450px)]! [&>div:nth-of-type(3)]:right-[calc(50%-450px)]!'
        )}
        classNameControls={classNameControls}
        classNameTrack={classNames('gap-4', classNameWrapper)}
        classNameSlide={classNames(
          'snap-start box-content',
          variantSlideClass,
          centerClass,
          classNameItems
        )}
        classNameIndicators={classNames(classNameIndicatorDots, classNameIndicators)}
        indicatorVariant={variationDot}
      />
    </div>
  )
}
